import { ChevronRightIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { Link, useLocation } from 'react-router';

import { AuthenticatedAppRouteTitle, AuthenticatedRoute } from '@/app/constant';
import { Home as HomeIcon } from '@/components/chariot-ui';
import { OverflowText } from '@/components/OverflowText';
import { Skeleton } from '@/components/ui/skeleton';
import { useActiveBulkJobs } from '@/hooks/useBulkJobs/useActiveBulkJobs';
import { useViewedTenant } from '@/hooks/useViewedTenant';
import { AccountDropdown } from '@/sections/topNavBar/AccountDropdown';
import { ActivityPill } from '@/sections/topNavBar/ActivityPill';
import { BulkJobsDrawer } from '@/sections/topNavBar/BulkJobsDrawer';
import { GlobalDownload } from '@/sections/topNavBar/GlobalDownload';
import { NotificationCenter } from '@/sections/topNavBar/NotificationCenter';
import { useAuth } from '@/state/auth';

export const TopNavBar: React.FC = () => {
  useActiveBulkJobs();

  const location = useLocation();

  const { whoami, isPraetorianUser } = useAuth();
  const { email, displayName } = useViewedTenant();
  const isNameLoading = !whoami;

  const pathNameSplit = location.pathname.split('/');
  const routeName = pathNameSplit[pathNameSplit.length - 1];
  const rawRouteTitle =
    AuthenticatedAppRouteTitle[routeName as AuthenticatedRoute];
  const routeTitle =
    routeName === AuthenticatedRoute.CUSTOMER_MANAGEMENT && !isPraetorianUser
      ? 'Tenant Management'
      : rawRouteTitle;

  return (
    <div
      className="flex w-full items-center gap-4 overflow-hidden px-9 py-3"
      data-tutorial="top-nav"
    >
      <div className="flex w-full items-center gap-3 overflow-hidden">
        <Link
          to={AuthenticatedRoute.INSIGHTS}
          relative="path"
          className="transition-opacity hover:opacity-70"
          aria-label="Navigate to Insights"
        >
          <HomeIcon className="text-chariot-text-secondary size-4 shrink-0 select-none" />
        </Link>
        <ChevronRightIcon
          style={{ strokeWidth: 4 }}
          className="text-chariot-text-secondary size-3 shrink-0 select-none"
        />
        {isNameLoading ? (
          <Skeleton className="w-20 shrink" />
        ) : (
          <Link
            to={AuthenticatedRoute.INSIGHTS}
            relative="path"
            className="transition-opacity hover:opacity-70"
            aria-label={`Navigate to ${displayName}`}
          >
            <OverflowText
              className="text-chariot-text-secondary text-sm font-semibold"
              text={displayName}
              data-testid="breadcrumb-name"
              data-email={email}
            />
          </Link>
        )}
        {routeTitle && (
          <>
            <ChevronRightIcon
              style={{ strokeWidth: 4 }}
              className="text-chariot-text-secondary size-3 shrink-0 select-none"
            />
            <p
              className="text-chariot-text-secondary text-sm font-semibold"
              data-testid="routebreadcrumb"
            >
              {routeTitle}
            </p>
          </>
        )}
      </div>
      <div className="ml-auto flex h-full shrink-0 items-center gap-5">
        <ActivityPill />
        <NotificationCenter />
        <BulkJobsDrawer />
        <GlobalDownload />
        <AccountDropdown />
      </div>
    </div>
  );
};
